/**
 * Branding Context
 * Fetches and applies organization branding colors to the application
 */

console.log('🎨 BrandingContext module loaded');

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { brandingAPI, OrganizationBranding } from '@/services/api/branding';

interface BrandingContextType {
  branding: OrganizationBranding | null;
  loading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
  isSandbox: boolean;
  sandboxExpiresAt: string | null;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
}

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance of a color
 * Uses the W3C formula for relative luminance
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determine if a color is dark (needs white text) or light (needs dark text)
 */
function isColorDark(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  return luminance < 0.5;
}

/**
 * Get the appropriate foreground color for a background color
 * Returns white for dark backgrounds, dark for light backgrounds
 */
function getContrastingForeground(hex: string): string {
  return isColorDark(hex) ? '#ffffff' : '#030213';
}

/**
 * Get or create the branding style element
 * Using a dedicated <style> element with :root selector ensures highest specificity
 */
function getBrandingStyleElement(): HTMLStyleElement {
  const STYLE_ID = 'org-branding-styles';
  let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    document.head.appendChild(styleEl);
  }

  return styleEl;
}

/**
 * Apply branding colors to CSS custom properties
 * Uses a dedicated <style> element with !important to override base CSS
 */
function applyBrandingColors(branding: OrganizationBranding | null) {
  console.log('🎨 Applying branding colors:', branding);

  const styleEl = getBrandingStyleElement();

  if (!branding) {
    console.log('🎨 No branding data, removing custom properties');
    // Clear the branding styles
    styleEl.textContent = '';
    return;
  }

  // Build CSS rules with !important to override base styles
  // We need to set both --primary and --color-primary for Tailwind 4 compatibility
  // Tailwind 4's @theme inline maps --color-primary to var(--primary), but we override both
  const cssRules: string[] = [];

  if (branding.brand_primary_color) {
    console.log('🎨 Setting --primary to:', branding.brand_primary_color);
    const primaryForeground = getContrastingForeground(branding.brand_primary_color);
    console.log('🎨 Setting --primary-foreground to:', primaryForeground);
    cssRules.push(`--brand-primary: ${branding.brand_primary_color} !important;`);
    cssRules.push(`--primary: ${branding.brand_primary_color} !important;`);
    cssRules.push(`--primary-foreground: ${primaryForeground} !important;`);
    // Tailwind 4 uses --color-primary for bg-primary, text-primary, etc.
    cssRules.push(`--color-primary: ${branding.brand_primary_color} !important;`);
    cssRules.push(`--color-primary-foreground: ${primaryForeground} !important;`);
  }

  if (branding.brand_secondary_color) {
    console.log('🎨 Setting --secondary to:', branding.brand_secondary_color);
    const secondaryForeground = getContrastingForeground(branding.brand_secondary_color);
    cssRules.push(`--brand-secondary: ${branding.brand_secondary_color} !important;`);
    cssRules.push(`--secondary: ${branding.brand_secondary_color} !important;`);
    cssRules.push(`--secondary-foreground: ${secondaryForeground} !important;`);
    cssRules.push(`--color-secondary: ${branding.brand_secondary_color} !important;`);
    cssRules.push(`--color-secondary-foreground: ${secondaryForeground} !important;`);
  }

  if (branding.brand_accent_color) {
    console.log('🎨 Setting --accent to:', branding.brand_accent_color);
    const accentForeground = getContrastingForeground(branding.brand_accent_color);
    cssRules.push(`--brand-accent: ${branding.brand_accent_color} !important;`);
    cssRules.push(`--accent: ${branding.brand_accent_color} !important;`);
    cssRules.push(`--accent-foreground: ${accentForeground} !important;`);
    cssRules.push(`--color-accent: ${branding.brand_accent_color} !important;`);
    cssRules.push(`--color-accent-foreground: ${accentForeground} !important;`);
  }

  if (branding.brand_text_color) {
    cssRules.push(`--brand-text: ${branding.brand_text_color} !important;`);
  }

  if (branding.brand_background_color) {
    cssRules.push(`--brand-background: ${branding.brand_background_color} !important;`);
  }

  // Sidebar tint from dedicated sidebar color or fallback to primary
  const sidebarHex = branding.brand_sidebar_color || branding.brand_primary_color;
  if (sidebarHex) {
    const sRgb = hexToRgb(sidebarHex);
    if (sRgb) {
      cssRules.push(`--sidebar-tint: rgba(${sRgb.r}, ${sRgb.g}, ${sRgb.b}, 0.08) !important;`);
      cssRules.push(`--sidebar-tint-hover: rgba(${sRgb.r}, ${sRgb.g}, ${sRgb.b}, 0.12) !important;`);
      cssRules.push(`--sidebar-border: rgba(${sRgb.r}, ${sRgb.g}, ${sRgb.b}, 0.15) !important;`);
      cssRules.push(`--sidebar-text-heading: rgba(${sRgb.r}, ${sRgb.g}, ${sRgb.b}, 0.7) !important;`);
    }
  }

  // Apply email colors as well for consistency
  if (branding.email_primary_color) {
    cssRules.push(`--email-primary: ${branding.email_primary_color} !important;`);
  }

  if (branding.email_secondary_color) {
    cssRules.push(`--email-secondary: ${branding.email_secondary_color} !important;`);
  }

  // Build override rules for hardcoded Tailwind blue classes used on buttons/interactive elements
  // This ensures branding colors apply to all existing components using bg-blue-600 etc.
  const overrideRules: string[] = [];
  if (branding.brand_primary_color) {
    const pc = branding.brand_primary_color;
    const fg = getContrastingForeground(pc);
    overrideRules.push(
      // Background overrides
      `.bg-blue-600 { background-color: ${pc} !important; }`,
      `.bg-blue-500 { background-color: ${pc} !important; }`,
      `.dark .dark\\:bg-blue-500 { background-color: ${pc} !important; }`,
      `.dark .dark\\:bg-blue-600 { background-color: ${pc} !important; }`,
      `.dark .dark\\:bg-blue-700 { background-color: ${pc} !important; }`,
      // Hover states
      `.hover\\:bg-blue-700:hover { background-color: ${pc} !important; filter: brightness(0.9); }`,
      `.hover\\:bg-blue-600:hover { background-color: ${pc} !important; filter: brightness(0.9); }`,
      `.dark .dark\\:hover\\:bg-blue-600:hover { background-color: ${pc} !important; filter: brightness(0.9); }`,
      // Gradient overrides (buttons using from-blue-600 to-indigo-600)
      `.from-blue-600 { --tw-gradient-from: ${pc} !important; }`,
      `.to-indigo-600 { --tw-gradient-to: ${pc} !important; }`,
      `.hover\\:from-blue-700:hover { --tw-gradient-from: ${pc} !important; filter: brightness(0.9); }`,
      `.hover\\:to-indigo-700:hover { --tw-gradient-to: ${pc} !important; filter: brightness(0.9); }`,
      // Text color overrides (links, ghost buttons)
      `.text-blue-600 { color: ${pc} !important; }`,
      `.text-blue-500 { color: ${pc} !important; }`,
      `.dark .dark\\:text-blue-400 { color: ${pc} !important; }`,
      `.dark .dark\\:text-blue-500 { color: ${pc} !important; }`,
      `.hover\\:text-blue-700:hover { color: ${pc} !important; filter: brightness(0.9); }`,
      `.hover\\:text-blue-800:hover { color: ${pc} !important; filter: brightness(0.85); }`,
      // Border overrides (outline buttons)
      `.border-blue-600 { border-color: ${pc} !important; }`,
      `.border-blue-500 { border-color: ${pc} !important; }`,
      `.dark .dark\\:border-blue-400 { border-color: ${pc} !important; }`,
      // Focus ring
      `.focus\\:ring-blue-500:focus { --tw-ring-color: ${pc} !important; }`,
      `.focus-visible\\:ring-blue-500:focus-visible { --tw-ring-color: ${pc} !important; }`,
      // Light background tints (hover states on ghost/outline buttons)
      `.bg-blue-50 { background-color: ${pc}10 !important; }`,
      `.hover\\:bg-blue-50:hover { background-color: ${pc}18 !important; }`,
      `.dark .dark\\:hover\\:bg-blue-900\\/30:hover { background-color: ${pc}30 !important; }`,
    );

    // Sidebar tint is now applied via inline styles in SidebarNavigation.tsx
    // using branding.brand_sidebar_color directly
  }

  // Page background override
  if (branding.brand_background_color && branding.brand_background_color !== '#FFFFFF') {
    overrideRules.push(
      `main { background-color: ${branding.brand_background_color} !important; }`,
    );
  }

  // Set the style content with :root selector + override rules
  if (cssRules.length > 0 || overrideRules.length > 0) {
    const rootBlock = cssRules.length > 0 ? `:root {\n  ${cssRules.join('\n  ')}\n}` : '';
    styleEl.textContent = [rootBlock, ...overrideRules].join('\n');
    console.log('🎨 Applied branding CSS variables and overrides');
  } else {
    styleEl.textContent = '';
  }
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  console.log('🎨 BrandingProvider rendering');
  const [branding, setBranding] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    // Get current organization ID and auth token from localStorage
    const cboId = localStorage.getItem('active_cbo_id');
    const authToken = localStorage.getItem('auth_token');

    if (!cboId || !authToken) {
      // No organization selected or not authenticated, clear branding
      setBranding(null);
      applyBrandingColors(null);
      return;
    }

    // Triveny is a non-CBO platform — /api/organization-branding is keyed by
    // healthcare cbo_id and rejects platform tokens. The Triveny design
    // system owns the brand via --v2-* tokens; no fetch needed.
    if (typeof window !== 'undefined' && window.location.hostname.includes('triveny.ai')) {
      setBranding(null);
      applyBrandingColors(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await brandingAPI.getBranding(parseInt(cboId, 10));
      setBranding(data);
      applyBrandingColors(data);
    } catch (err: any) {
      console.error('Failed to fetch branding:', err);
      setError(err.message || 'Failed to load branding');
      // Don't clear branding on error, keep previous state
    } finally {
      setLoading(false);
    }
  }, []);

  // Consolidated useEffect: Fetch branding on mount and when org/auth changes
  // Combines storage, organization, and auth event listeners to prevent multiple renders
  useEffect(() => {
    // Initial fetch
    fetchBranding();

    // Handle storage events (when active_cbo_id changes in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'active_cbo_id' || e.key === 'auth_token') {
        if (e.key === 'auth_token' && !e.newValue) {
          // Token was removed (logout)
          setBranding(null);
          applyBrandingColors(null);
        } else {
          fetchBranding();
        }
      }
    };

    // Handle organization change within the same tab
    const handleOrgChange = () => {
      fetchBranding();
    };

    // Handle auth state changes (login/logout)
    const handleAuthChange = () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        fetchBranding();
      } else {
        setBranding(null);
        applyBrandingColors(null);
      }
    };

    // Handle branding save from settings page
    const handleBrandingChange = () => {
      fetchBranding();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('organizationChanged', handleOrgChange);
    window.addEventListener('authStateChanged', handleAuthChange);
    window.addEventListener('brandingChanged', handleBrandingChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('organizationChanged', handleOrgChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('brandingChanged', handleBrandingChange);
    };
  }, [fetchBranding]);

  const isSandbox = !!(branding as any)?.is_sandbox;
  const sandboxExpiresAt = (branding as any)?.sandbox_expires_at || null;

  const value = {
    branding,
    loading,
    error,
    refreshBranding: fetchBranding,
    isSandbox,
    sandboxExpiresAt,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

export default BrandingContext;
